import { isValid, parseISO } from "date-fns";
import type {
	AlertBoxActionEnum,
	PortfolioAlertBoxDTO,
} from "web_myplann/Infrastructure/Api/Api";
import {
	type AlertBoxActionState,
	alertActionStateSchema,
} from "web_myplann/NotificationCenter/EventAlert/alertActionStateSchema";

export type ParsedAlertActionState = {
	state: AlertBoxActionState;
	performedAt: Date;
};

const statePrecedence: Record<AlertBoxActionState, number> = {
	Sent: 1,
	Opened: 0,
};

const isMorePrecedent = (
	candidate: ParsedAlertActionState,
	current: ParsedAlertActionState,
): boolean => {
	const candidatePrecedence = statePrecedence[candidate.state];
	const currentPrecedence = statePrecedence[current.state];
	if (candidatePrecedence !== currentPrecedence) {
		return candidatePrecedence > currentPrecedence;
	}
	return candidate.performedAt.getTime() > current.performedAt.getTime();
};

export const parseAlertActionStates = (
	alert: PortfolioAlertBoxDTO,
): Partial<Record<AlertBoxActionEnum, ParsedAlertActionState>> => {
	const rawPerformedActions = alert.performedActions;

	if (!Array.isArray(rawPerformedActions)) {
		return {};
	}

	const result: Partial<Record<AlertBoxActionEnum, ParsedAlertActionState>> =
		{};

	for (const rawPerformedAction of rawPerformedActions) {
		const parsed = alertActionStateSchema.safeParse(rawPerformedAction);
		if (!parsed.success) {
			continue;
		}

		const performedAt = parseISO(parsed.data.performedAt);
		if (!isValid(performedAt)) {
			continue;
		}

		const candidate: ParsedAlertActionState = {
			state: parsed.data.state,
			performedAt,
		};

		const current = result[parsed.data.action];
		if (!current || isMorePrecedent(candidate, current)) {
			result[parsed.data.action] = candidate;
		}
	}

	return result;
};
